import React from 'react';
import type { ResultStrategy, WidgetDefinition, EvaluatePanelPropsBase } from './ResultStrategy';
import type { Layouts } from 'react-grid-layout';

// Widgets
import { QuantArcEquityWidget } from '../QuantArcEquityWidget';
import { QuantArcExecutionWidget } from '../QuantArcExecutionWidget';
import { AiInsightWidget } from '../AiInsightWidget';
import { WidgetWrapper } from '../WidgetWrapper';

export class PnLStrategy implements ResultStrategy {
    id = 'pnl';

    match(nature?: string): boolean {
        return nature === 'result';
    }

    getAvailableWidgets(hasAiReport: boolean): WidgetDefinition[] {
        const widgets = [
            { id: 'summary', label: '📊 Performance Summary', icon: '📊' },
            { id: 'equity', label: '📈 Performance', icon: '📈' },
            { id: 'execution', label: '🎯 Execution & Position', icon: '🎯' },
        ];
        if (hasAiReport) {
            widgets.push({ id: 'ai-insights', label: '🤖 AI Insights', icon: '🤖' });
        }
        return widgets;
    }

    getDefaultWidgets(): string[] {
        return ['summary', 'equity', 'execution'];
    }

    getLayout(activeWidgets: string[]): Layouts {
        return {
            lg: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 3 },
                { i: 'equity', x: 0, y: 3, w: 6, h: 6 },
                { i: 'execution', x: 6, y: 3, w: 6, h: 6 },
                { i: 'ai-insights', x: 0, y: 9, w: 12, h: 4 },
            ].filter((item) => activeWidgets.includes(item.i)),
            md: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 4 },
                { i: 'equity', x: 0, y: 4, w: 12, h: 6 },
                { i: 'execution', x: 0, y: 10, w: 12, h: 6 },
                { i: 'ai-insights', x: 0, y: 16, w: 12, h: 6 },
            ].filter((item) => activeWidgets.includes(item.i)),
            sm: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 4 },
                { i: 'equity', x: 0, y: 4, w: 12, h: 5 },
                { i: 'execution', x: 0, y: 9, w: 12, h: 5 },
                { i: 'ai-insights', x: 0, y: 14, w: 12, h: 6 },
            ].filter((item) => activeWidgets.includes(item.i)),
            xs: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 4 },
                { i: 'equity', x: 0, y: 4, w: 12, h: 5 },
                { i: 'execution', x: 0, y: 9, w: 12, h: 5 },
                { i: 'ai-insights', x: 0, y: 14, w: 12, h: 6 },
            ].filter((item) => activeWidgets.includes(item.i)),
            xxs: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 4 },
                { i: 'equity', x: 0, y: 4, w: 12, h: 5 },
                { i: 'execution', x: 0, y: 9, w: 12, h: 5 },
                { i: 'ai-insights', x: 0, y: 14, w: 12, h: 6 },
            ].filter((item) => activeWidgets.includes(item.i)),
        };
    }

    getStats(data: any, backendStats?: Record<string, number | null> | null): React.ReactNode {
        const stats = (() => {
            if (backendStats) {
                return {
                    totalTrades: backendStats.total_trades ?? 0,
                    totalProfit: backendStats.total_profit as number ?? 0,
                    winRate: backendStats.win_rate as number ?? 0,
                    profitFactor: backendStats.profit_factor as number | null,
                    maxDrawdown: backendStats.max_drawdown as number ?? 0,
                    avgTrade: backendStats.avg_trade as number ?? 0,
                };
            }
            if (!data?.length) return null;
            const values = data.map((d: any) => d.value).filter((v: any) => typeof v === "number");
            const count = values.length;
            if (count === 0) return null;

            const latest = values[values.length - 1];
            const returns = values.map((v: number, i: number) => i === 0 ? v : v - values[i - 1]);
            const wins = returns.filter((r: number) => r > 0);
            const losses = returns.filter((r: number) => r <= 0);

            let peak = values[0];
            let maxDrawdown = 0;
            for (const v of values) {
                if (v > peak) peak = v;
                const drawdown = peak - v;
                if (drawdown > maxDrawdown) maxDrawdown = drawdown;
            }

            const grossProfit = wins.reduce((sum: number, r: number) => sum + r, 0);
            const grossLoss = Math.abs(losses.reduce((sum: number, r: number) => sum + r, 0));

            return {
                totalTrades: count,
                totalProfit: latest,
                winRate: count > 0 ? wins.length / count : 0,
                profitFactor: grossLoss > 0 ? grossProfit / grossLoss : (grossProfit > 0 ? Infinity : 0),
                maxDrawdown,
                avgTrade: count > 0 ? latest / count : 0,
            };
        })();

        if (!stats) return null;

        const formatCurrency = (val: number) => {
            const abs = Math.abs(val);
            if (abs > 1_000_000) return (val / 1_000_000).toFixed(2) + 'm';
            if (abs > 1_000) return (val / 1_000).toFixed(2) + 'k';
            return val.toFixed(2);
        };
        const formatPercent = (val: number) => (val * 100).toFixed(1) + '%';

        const cards = [
            { label: 'Total Trades', value: stats.totalTrades.toString(), color: 'var(--text-primary)' },
            { 
                label: 'Total Profit', 
                value: (stats.totalProfit > 0 ? '+' : '') + formatCurrency(stats.totalProfit),
                color: stats.totalProfit >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)'
            },
            { 
                label: 'Avg Trade PnL', 
                value: (stats.avgTrade > 0 ? '+' : '') + formatCurrency(stats.avgTrade),
                color: stats.avgTrade >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)'
            },
            { label: 'Win Rate', value: formatPercent(stats.winRate), color: 'var(--text-primary)' },
            { label: 'Profit Factor', value: stats.profitFactor === Infinity || stats.profitFactor == null ? '∞' : stats.profitFactor.toFixed(2), color: 'var(--text-primary)' },
            { label: 'Max Drawdown', value: '-' + formatCurrency(stats.maxDrawdown), color: 'var(--accent-danger)' },
        ];

        return (
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                gap: '12px',
                padding: '8px' 
            }}>
                {cards.map((card, idx) => (
                    <div key={idx} style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        padding: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        transition: 'all 0.2s ease',
                    }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {card.label}
                        </span>
                        <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: card.color, fontFamily: 'monospace' }}>
                            {card.value}
                        </span>
                    </div>
                ))}
            </div>
        );
    }

    renderWidget(widgetId: string, props: EvaluatePanelPropsBase): React.ReactNode {
        switch (widgetId) {
            case 'equity':
                return <QuantArcEquityWidget data={props.result ?? []} />;
            case 'execution':
                return (
                    <QuantArcExecutionWidget
                        data={props.result ?? []}
                        chartData={props.chartData ?? null}
                        chartSeriesLabels={props.chartSeriesLabels ?? null}
                    />
                );
            case 'summary':
                return (
                    <WidgetWrapper title="📊 Performance Summary">
                        {this.getStats(props.result, props.backendStats)}
                    </WidgetWrapper>
                );
            case 'ai-insights':
                if (!props.currentAiReport) return null;
                return <AiInsightWidget report={props.currentAiReport} />;
            default:
                return null;
        }
    }
}
